/**
 * =============================================================================
 * FETCH WITH RETRY UTILITY
 * =============================================================================
 *
 * Utility function for making fetch requests with automatic retry
 * and exponential backoff on failure.
 *
 * PATTERN REFERENCE: PHASE_0C_PATTERNS.md §8
 * TASK: 0C-39
 *
 * Features:
 * - Exponential backoff (1s, 2s, 4s by default)
 * - Configurable max retries
 * - Only retries on network errors and 5xx server errors
 * - Does not retry 4xx client errors
 * - Offline detection before each attempt (including retries)
 *
 * @example
 * ```typescript
 * // Basic usage
 * const response = await fetchWithRetry('/api/data');
 *
 * // With custom options
 * const response = await fetchWithRetry('/api/data', {
 *   method: 'POST',
 *   body: JSON.stringify(data),
 * }, { maxRetries: 5, baseDelayMs: 500 });
 * ```
 *
 * =============================================================================
 */

/**
 * Default request timeout in milliseconds (30 seconds).
 * Requests that exceed this duration will be aborted with a user-friendly error.
 * P1-11: AbortController for API Request Timeouts
 */
export const DEFAULT_REQUEST_TIMEOUT_MS = 30000;

/**
 * Configuration options for fetchWithRetry.
 */
export interface RetryOptions {
  /** Maximum number of retry attempts. Default: 3 */
  maxRetries?: number;
  /** Base delay in milliseconds for exponential backoff. Default: 1000 */
  baseDelayMs?: number;
  /** HTTP status codes that should trigger a retry. Default: 5xx errors */
  retryableStatuses?: number[];
  /** Request timeout in milliseconds. Default: 30000 (30s). P1-11 */
  timeoutMs?: number;
}

/**
 * Error thrown when the user is offline.
 */
export class OfflineError extends Error {
  constructor() {
    super('You appear to be offline. Please check your connection and try again.');
    this.name = 'OfflineError';
  }
}

/**
 * Error thrown when max retries are exceeded.
 */
export class MaxRetriesExceededError extends Error {
  public readonly attempts: number;
  public readonly lastError: unknown;

  constructor(attempts: number, lastError: unknown) {
    const message =
      lastError instanceof Error
        ? `Request failed after ${attempts} attempts: ${lastError.message}`
        : `Request failed after ${attempts} attempts`;
    super(message);
    this.name = 'MaxRetriesExceededError';
    this.attempts = attempts;
    this.lastError = lastError;
  }
}

/**
 * Error thrown when a request times out.
 * P1-11: AbortController for API Request Timeouts
 */
export class RequestTimeoutError extends Error {
  constructor() {
    super('The request timed out. Please check your connection and try again.');
    this.name = 'RequestTimeoutError';
  }
}

/**
 * Default retry options.
 */
const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  baseDelayMs: 1000,
  // Retry on 5xx server errors - client shouldn't retry on 4xx
  retryableStatuses: [500, 502, 503, 504],
  // P1-11: Default 30s timeout
  timeoutMs: DEFAULT_REQUEST_TIMEOUT_MS,
};

/**
 * Combine multiple abort signals into one.
 * Uses native AbortSignal.any when available, falls back to polyfill.
 *
 * Review fix: MULTI-TASK-REVIEW-POST-P1-23
 * - Consumer-provided signal was being overwritten by timeout signal
 * - This helper combines both signals so consumers can cancel requests
 *
 * @param signals - Array of signals to combine (undefined values filtered out)
 * @returns Combined signal that aborts when any input signal aborts
 */
function anySignal(signals: Array<AbortSignal | null | undefined>): AbortSignal {
  const active = signals.filter((s): s is AbortSignal => s != null);
  if (active.length === 0) return new AbortController().signal;
  if (active.length === 1) return active[0];

  // Prefer native AbortSignal.any when available (modern browsers)
  const signalAny = (AbortSignal as unknown as { any?: (s: AbortSignal[]) => AbortSignal }).any;
  if (signalAny) return signalAny(active);

  // Fallback: create controller that aborts when any input aborts
  const controller = new AbortController();
  const onAbort = (): void => controller.abort();
  for (const s of active) {
    if (s.aborted) {
      controller.abort();
      break;
    }
    s.addEventListener('abort', onAbort, { once: true });
  }
  return controller.signal;
}

/**
 * Check if a status code should trigger a retry.
 */
function isRetryableStatus(status: number, retryableStatuses: number[]): boolean {
  return retryableStatuses.includes(status);
}

/**
 * Delay execution for the specified duration.
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Make a fetch request with automatic retry, exponential backoff, and timeout.
 *
 * Behavior:
 * - Checks if the browser is online before each attempt (including retries)
 * - Retries on network errors and specified server errors (5xx by default)
 * - Does NOT retry on client errors (4xx) since those are permanent failures
 * - Uses exponential backoff: 1s, 2s, 4s, etc.
 * - Each request attempt has a configurable timeout (default 30s) - P1-11
 * - Timeouts are NOT retried (considered a network failure)
 *
 * @param url - The URL to fetch
 * @param init - Standard RequestInit options
 * @param options - Retry configuration options
 * @returns The fetch Response
 * @throws OfflineError if the browser is offline
 * @throws RequestTimeoutError if the request times out (P1-11)
 * @throws MaxRetriesExceededError if all retry attempts fail
 */
export async function fetchWithRetry(
  url: string,
  init: RequestInit = {},
  options: RetryOptions = {}
): Promise<Response> {
  const { maxRetries, baseDelayMs, retryableStatuses, timeoutMs } = {
    ...DEFAULT_OPTIONS,
    ...options,
  };

  let lastError: unknown;
  const totalAttempts = maxRetries + 1; // 1 initial + maxRetries

  for (let attempt = 0; attempt < totalAttempts; attempt++) {
    // Check online status before each attempt (may have changed during backoff)
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw new OfflineError();
    }

    // P1-11: Create AbortController for timeout handling (per-attempt)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      // Combine consumer signal with timeout signal (review fix: signal override)
      const response = await fetch(url, {
        ...init,
        signal: anySignal([init.signal, controller.signal]),
      });

      // Success or non-retryable error (4xx client errors)
      if (response.ok || !isRetryableStatus(response.status, retryableStatuses)) {
        return response;
      }

      // Retryable server error - will retry if attempts remain
      lastError = new Error(`Server error: ${response.status} ${response.statusText}`);
    } catch (error) {
      // P1-11: Handle timeout (AbortError) - throw immediately, no retry
      if (error instanceof Error && error.name === 'AbortError') {
        throw new RequestTimeoutError();
      }

      // Network error (offline, DNS, CORS, etc.)
      lastError = error;
    } finally {
      clearTimeout(timeoutId);
    }

    // Don't delay after the last attempt
    if (attempt < totalAttempts - 1) {
      const delayMs = baseDelayMs * Math.pow(2, attempt);
      await delay(delayMs);
    }
  }

  throw new MaxRetriesExceededError(totalAttempts, lastError);
}

/**
 * Wrapper to handle retry results with a success/error response format.
 *
 * Useful when you want to avoid throwing errors and instead handle
 * failures as data.
 *
 * @param fn - Async function to execute with retry logic
 * @returns Object with success flag and either data or error
 */
export async function withRetryResult<T>(
  fn: () => Promise<T>
): Promise<{ success: true; data: T } | { success: false; error: string; isOffline: boolean }> {
  try {
    const data = await fn();
    return { success: true, data };
  } catch (error) {
    const isOffline = error instanceof OfflineError;
    const message =
      error instanceof Error ? error.message : 'An unexpected error occurred';
    return { success: false, error: message, isOffline };
  }
}
